import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { selectSession, SessionActions } from '@state/session';
import { catchError, filter, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { CARGOCHIEF_KEYS, PAGE_SIZE } from '@domain/constants/app-variables';
import {
	ADD_DAYS,
	DATE_YMD,
	IS_LESS_THAN_CURRENT_TIMESTAMP,
	IS_VALID_DATE,
	RestService,
} from 'cargo-chief-commons';
import { environment } from '@environment/environment';
import { EMPTY, forkJoin, of } from 'rxjs';
import { UrlService } from '@service/url/url.service';

@Injectable({
	providedIn: 'root',
})
export class AuthGuardLegacyEffects {
	private static readonly invalidCodes = [401, 422];
	private static readonly invalidKeywords = ['unauthorized', 'session'];

	constructor(
		private store: Store,
		private actions$: Actions<any>,
		private cargoChief: Cargochief,
		private restService: RestService,
		private urlService: UrlService
	) {}

	private static parseError(err) {
		if (
			AuthGuardLegacyEffects.invalidCodes.includes(err?.status) &&
			AuthGuardLegacyEffects.invalidKeywords.some((keyword) =>
				err?.error?.toLowerCase()?.includes(keyword)
			)
		) {
			return of(SessionActions.validate.fail());
		}

		return of(EMPTY);
	}

	routerNavigated$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ROUTER_NAVIGATED),
			switchMap(() => this.store.select(selectSession).pipe(take(1))),
			filter((session) => session?.loggedIn === true),
			mergeMap((session) =>
				forkJoin([
					this.restService.GET(this.urlService.userCarrierMap()).pipe(
						map((response) => response.data),
						catchError(AuthGuardLegacyEffects.parseError)
					),
					this.restService
						.GET(
							this.urlService.getAllAccountUsers(
								session.data.account.id,
								0,
								PAGE_SIZE.USERS_LIST_CACHE
							)
						)
						.pipe(
							map((response) => response.data),
							catchError(AuthGuardLegacyEffects.parseError)
						),
					this.restService.GET(this.urlService.notificationSettings()).pipe(
						map((response) => response.data),
						catchError(AuthGuardLegacyEffects.parseError)
					),
					this.restService.GET(this.urlService.integrationTypesUrl()).pipe(
						map((response) => response.data),
						catchError(AuthGuardLegacyEffects.parseError)
					),
				]).pipe(map((data) => [session, ...data]))
			),
			map(([session, userCarrierMap, allAccountUsers, notificationSettings, integrationTypes]) => {
				const invalidResponse = [
					userCarrierMap,
					allAccountUsers,
					notificationSettings,
					integrationTypes,
				].find((request) => request.type === SessionActions.validate.fail.type);

				if (invalidResponse) {
					return invalidResponse;
				}
				this.cargoChief.setItem({ [CARGOCHIEF_KEYS.USER_CARRIER_MAPS]: userCarrierMap });
				this.cargoChief.setItem({ [CARGOCHIEF_KEYS.ACCOUNT_USERS]: allAccountUsers });
				this.cargoChief.setItem({
					[CARGOCHIEF_KEYS.NOTIFICATION_SETTINGS]: notificationSettings,
				});
				this.cargoChief.setItem({
					[CARGOCHIEF_KEYS.INTEGRATION_TYPES]: integrationTypes,
				});

				this.cargoChief.setItem({
					[CARGOCHIEF_KEYS.PAYMENT_AMOUNT]: session.data.account.planAmount,
				});
				this.cargoChief.setItem({
					[CARGOCHIEF_KEYS.NEXT_PAYMENT_DATE_EXPIRED]:
						!session.data.account.nextPaymentDate ||
						(IS_VALID_DATE(DATE_YMD(session.data.account.nextPaymentDate), ['Y-M-D']) &&
							IS_LESS_THAN_CURRENT_TIMESTAMP(
								DATE_YMD(
									ADD_DAYS(session.data.account.nextPaymentDate, environment.PAYMENT_TRANSIT_DAYS)
								)
							)),
				});

				return SessionActions.cache.update();
			})
		)
	);
}
